<template>
	<v-dialog
		id="bulkEdit"
		v-model="showDialog"
		max-width="800"
		:scrollable="true"
		persistent
	>
		<v-card>
			<v-card-title> Bulk Edit </v-card-title>
			<v-card-text>
				<div v-if="isVisible" class="grid-flex-container">
					<v-form
						ref="form"
						class="d-sm-block flex-column flex-wrap flex-grow-1"
						:class="getOverflowYClass"
					>
						<div
							:class="getFormFieldClass"
							v-for="(field, index) in formFields"
							:key="index"
						>
							<form-field
								:ref="getFormFieldRef(field)"
								v-if="field != null && record"
								:formField="field"
								:record="record"
								:tabIndex="getTabIndex(field)"
								:uniqueFields="uniqueFieldsToCheckIds"
								:isBulkEdit="true"
								@removeFieldToCheckId="onRemoveFieldToCheckId"
							/>
						</div>
					</v-form>
					<CheckLinkFieldsModal
						v-if="
							checkLinkFields_showDialog &&
							checkLinkFields_foreignKey &&
							checkLinkFields_gridView
						"
						@close="onCheckLinkFieldsModalClose"
						@submit="onCheckLinkFieldsModalSubmit"
						:dialog="checkLinkFields_showDialog"
						:gridView="checkLinkFields_gridView"
						:foreignKey="checkLinkFields_foreignKey"
					/>
					<CheckReferenceFieldsModal
						v-if="
							checkReferenceFields_showDialog &&
							checkReferenceField_referenceField
						"
						:dialog="checkReferenceFields_showDialog"
						:referenceField="checkReferenceField_referenceField"
						@close="onCheckReferenceFieldsModalClose"
						@submit="onCheckReferenceFieldsModalSubmit"
					/>
					<UniqueValuesDialog
						v-if="showUniqueValuesDialog"
						:showDialog="showUniqueValuesDialog"
						:fields="uniqueFieldsToCheckLabels"
						@confirm="confirmUniqueFields"
					/>
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn @click.stop="onClose">Close</v-btn>
				<v-btn color="primary" @click.stop="onSave">
					<v-icon class="mr-2">mdi-content-save</v-icon>
					Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import CheckLinkFieldsModal from '../form/CheckLinkFieldsModal.vue';
import CheckReferenceFieldsModal from '../form/CheckReferenceFieldsModal.vue';
import FormField from '../form/FormField.vue';
import dataService from '../../../services/data/inventoryFormService';
import {
	InventoryFormFormActions,
	InventoryFormGridActions,
} from '../../../store/actions';
import {
	InventoryFormFormGetters,
	AuthGetterTypes,
} from '../../../store/getters';
import { mapGetters } from 'vuex';
import { FieldDataTypes } from '../../../enums/fieldDataTypes';
import gridViewFieldsUtils from '../../../utils/gridViewFieldsUtils';
import checkLinkFieldsMixin from '../../../mixins/inventoryForm/checkLinkFieldsMixin';
import checkReferenceFieldsMixin from '../../../mixins/inventoryForm/checkReferenceFieldsMixin';
import { formFieldRule } from '../../../enums/formFieldRuleEnum';
import UniqueValuesDialog from './UniqueValuesDialog.vue';

export default {
	name: 'BulkEditFormModal',
	components: {
		FormField,
		CheckLinkFieldsModal,
		CheckReferenceFieldsModal,
		UniqueValuesDialog,
	},
	mixins: [checkLinkFieldsMixin, checkReferenceFieldsMixin],
	props: {
		showDialog: Boolean,
		selectedRows: Array,
	},
	data() {
		return {
			showUniqueValuesDialog: false,
			uniqueFieldsToCheckLabels: [],
			uniqueFieldsToCheckIds: [],
		};
	},

	computed: {
		getOverflowYClass() {
			if (this.columns) {
				if (this.columns == 1) return 'overflow-y-auto';
				if (!this.$vuetify.breakpoint.mdAndUp) return 'overflow-y-auto';
				return 'd-md-flex overflow-y-hidden';
			}
			return 'overflow-y-auto';
		},

		getFormFieldClass() {
			if (!this.$vuetify.breakpoint.mdAndUp) return 'formField-sm';
			if (!this.columns) return 'formField-md1';
			switch (this.columns) {
				case 1:
					return 'formField-md1';
				case 2:
					return 'formField-md2';
				case 3:
					return 'formField-md3';
				default:
					return 'formField-md1';
			}
		},
		...mapGetters({
			formFields: [InventoryFormFormGetters.FORM_FIELDS],
			hiddenFields: [InventoryFormFormGetters.HIDDEN_FORM_FIELDS],
			columns: [InventoryFormFormGetters.FORM_COLUMNS],
			defaultGridView: [InventoryFormFormGetters.DEFAULT_GRID_VIEW],
			record: [InventoryFormFormGetters.RECORD],
			selectedInventoryForm: [
				InventoryFormFormGetters.SELECTED_INVENTORY_FORM,
			],
			currentUserId: [AuthGetterTypes.CURRENT_USER_ID],
		}),
		isVisible() {
			if (!this.record) return false;
			if (!this.formFields) return false;
			if (!this.columns) return false;
			if (!this.defaultGridView) return false;
			return true;
		},

		formFieldsRefs() {
			if (!this.formFields) return [];
			return this.formFields.map((x) => 'formField_' + x.Id);
		},

		currentInventoryFormId() {
			return this.$route.params.formId;
		},
		recordId() {
			if (!this.record) return null;
			return this.record.Id;
		},
		headerColor() {
			return this.hasRecordId ? '#ffc00030' : '#ffffff';
		},
	},
	watch: {
		currentInventoryFormId: function () {
			this.loadInventoryForm();
		},
	},
	mounted() {
		this.loadInventoryForm().then(() => {
			if (!this.record)
				this.$store.dispatch(InventoryFormFormActions.LOAD_RECORD, {
					record: {},
					userId: this.currentUserId,
				});
			else
				this.$store.dispatch(InventoryFormFormActions.LOAD_RECORD, {
					record: this.record,
					userId: this.currentUserId,
				});
		});
	},
	destroyed() {
		this.$store.dispatch(InventoryFormFormActions.CLEAR);
	},
	methods: {
		onRemoveFieldToCheckId(id) {
			if (this.uniqueFieldsToCheckIds.length > 0) {
				let index = this.uniqueFieldsToCheckIds.findIndex(
					(e) => e === id
				);
				if (index >= 0) this.uniqueFieldsToCheckIds.splice(index, 1);
			}
		},
		checkUniqueValues() {
			let result = [];
			this.selectedInventoryForm.FormFields.forEach((x) => {
				let object = x.FormFieldRules.find(
					(x) => x.Operator == formFieldRule.MaxOne
				);

				if (object) {
					result.push(x);
				}
			});
			result = result.map((x) => ({
				EntityName: x.GridViewFields[0].DataField.EntityName,
				FieldName: x.GridViewFields[0].DataField.FieldName,
				FieldId: x.GridViewFields[0].DataField.Id,
				Value: this.record[
					gridViewFieldsUtils.getFieldName(x.GridViewFields[0])
				],
			}));
			return new Promise((resolve) => {
				dataService
					.checkUniqueValues(result)
					.then((resp) => resolve(resp.data));
			});
		},
		loadInventoryForm() {
			return this.$store.dispatch(
				InventoryFormFormActions.LOAD_INVENTORY_FORM,
				this.currentInventoryFormId
			);
		},
		getFormFieldRef(field) {
			let ref = 'formField_' + field.Id;
			return ref;
		},
		getTabIndex(field) {
			let offset = 1;
			if (this.formFields && this.formFields.length > 0) {
				let index = this.formFields.indexOf(field);
				if (index > 0) offset = offset + index;
			}
			return 100 + offset;
		},

		onClose() {
			this.$emit('close');
		},
		async onSave() {
			this.uniqueFieldsToCheckLabels = [];
			this.uniqueFieldsToCheckIds = [];
			if (!this.validate()) {
				return;
			}

			this.handleHiddenFields();
			if (!this.validate()) {
				return;
			}

			let checkLinkFields = await this.checkLinkFields();
			if (!checkLinkFields) {
				return;
			}

			let checkReferenceFields = await this.checkReferenceFields();
			if (!checkReferenceFields) return;

			let checkUniqueValue = await this.checkUniqueValues();

			if (checkUniqueValue.ExistingConflicts === true) {
				let fields = [];
				this.selectedInventoryForm.FormFields.forEach((x) =>
					x.GridViewFields.forEach((gvf) => {
						if (
							checkUniqueValue.FieldNames.includes(
								gvf.DataFieldName
							)
						)
							fields.push(gvf.Label);
					})
				);
				this.uniqueFieldsToCheckLabels = fields;
				this.uniqueFieldsToCheckIds = checkUniqueValue.FieldIds;
				this.showUniqueValuesDialog = true;
				this.validate();
				return;
			}
			if (Object.keys(this.record).length == 0) {
				return;
			}
			this.$store
				.dispatch(InventoryFormGridActions.BULK_EDIT, {
					InventoryFormId: this.$route.params.formId,
					SelectedRows: this.selectedRows,
					Record: JSON.stringify(this.record),
				})
				.then(() => {
					this.$emit('save');
				});
		},
		confirmUniqueFields() {
			this.showUniqueValuesDialog = false;
		},
		handleHiddenFields() {
			this.hiddenFields.forEach((x) => {
				switch (x.GridViewFields[0].DataField.DataType) {
					case FieldDataTypes.DATETIME: {
						let key = gridViewFieldsUtils.getFieldName(
							x.GridViewFields[0]
						);
						let value = [
							{
								key: key,
								// value: new Date().toISOString().split("T")[0],
								value: new Date().toUTCString(),
							},
						];
						this.$store.dispatch(
							InventoryFormFormActions.UPDATE_RECORD_KEY,
							value
						);
						break;
					}
					case FieldDataTypes.USER: {
						let key = gridViewFieldsUtils.getFieldName(
							x.GridViewFields[0]
						);
						let value = [{ key: key, value: this.currentUserId }];
						this.$store.dispatch(
							InventoryFormFormActions.UPDATE_RECORD_KEY,
							value
						);
						break;
					}
				}
			});
		},
		validate() {
			return this.$refs['form'].validate();
		},
	},
};
</script>
